import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "./button";
import { Heading } from "./heading";
import { showAlert } from "../alertService";
import { TrashIcon, PlusIcon, LanguageIcon } from "@heroicons/react/20/solid";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions,
  DialogInput,
} from "./dialog";

const OpenDataManager = () => {
  const [tiposVenue, setTiposVenue] = useState([]);
  // Traducciones EN de tipos, mapa {tipoES: tipoEN} en openData/tiposVenues.
  // El homesite /en las consume vía Flask /catalogs; sin traducción cae al ES.
  const [tiposEn, setTiposEn] = useState({});
  const [enDialogTipo, setEnDialogTipo] = useState(null);
  const [enDialogValue, setEnDialogValue] = useState("");
  const [capacidades, setCapacidades] = useState([]);
  const [precios, setPrecios] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [newItemValue, setNewItemValue] = useState("");
  const [currentAction, setCurrentAction] = useState({
    arrayName: "",
    docName: "",
    fieldName: "",
    index: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const collections = [
        { name: "tiposVenues", setter: setTiposVenue, arrayField: "tipos" },
        {
          name: "capacidad",
          setter: setCapacidades,
          arrayField: "capacidades",
        },
        //{ name: 'precios', setter: setPrecios, arrayField: 'precios' },
        { name: "zonas", setter: setZonas, arrayField: "zonas" },
      ];

      for (const collection of collections) {
        const docRef = doc(db, "openData", collection.name);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          collection.setter(docSnap.data()[collection.arrayField] || []);
          if (collection.name === "tiposVenues") {
            setTiposEn(docSnap.data().tiposEn || {});
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showAlert("Error al cargar los datos", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result, arrayName) => {
    if (!result.destination) return;

    const items = Array.from(getArrayByName(arrayName));
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    updateArrayByName(arrayName, items);
  };

  const getArrayByName = (name) => {
    switch (name) {
      case "tipos":
        return tiposVenue;
      case "capacidades":
        return capacidades;
      case "precios":
        return precios;
      case "zonas":
        return zonas;
      default:
        return [];
    }
  };

  const updateArrayByName = (name, newArray) => {
    console.log(newArray);
    switch (name) {
      case "tipos":
        setTiposVenue(newArray);
        break;
      case "capacidades":
        setCapacidades(newArray);
        break;
      case "precios":
        setPrecios(newArray);
        break;
      case "zonas":
        setZonas(newArray);
        break;
      default:
        console.warn(`Unexpected array name: ${name}`);
        break;
    }
  };

  const handleSave = async () => {
    try {
      console.log(getArrayByName(currentAction.arrayName));
      console.log(currentAction.fieldName);
      console.log(currentAction.docName);
      setSaving(true);
      const docRef = doc(db, "openData", currentAction.docName);
      const payload = {
        [currentAction.fieldName]: getArrayByName(currentAction.arrayName),
      };
      // Al guardar tipos, poda traducciones de tipos que ya no existen.
      if (currentAction.docName === "tiposVenues") {
        const vivos = getArrayByName(currentAction.arrayName);
        const pruned = Object.fromEntries(
          Object.entries(tiposEn).filter(([es]) => vivos.includes(es))
        );
        payload.tiposEn = pruned;
        setTiposEn(pruned);
      }
      await updateDoc(docRef, payload);
      setIsSaveDialogOpen(true);
    } catch (error) {
      console.error("Error saving data:", error);
      showAlert("Error al guardar los datos", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = () => {
    if (newItemValue.trim()) {
      const currentArray = getArrayByName(currentAction.arrayName);
      updateArrayByName(currentAction.arrayName, [
        ...currentArray,
        newItemValue.trim(),
      ]);
      setNewItemValue("");
      setIsAddDialogOpen(false);
    }
  };

  const handleRemoveItem = () => {
    const currentArray = getArrayByName(currentAction.arrayName);
    const newArray = currentArray.filter((_, i) => i !== currentAction.index);
    updateArrayByName(currentAction.arrayName, newArray);
    setIsDeleteDialogOpen(false);
  };

  // Guarda la traducción EN de un tipo de inmediato (no espera "Guardar
  // Cambios" — no toca el array `tipos`, solo el mapa `tiposEn`).
  const handleSaveEn = async () => {
    const tipo = enDialogTipo;
    const trimmed = enDialogValue.trim();
    const next = { ...tiposEn };
    if (trimmed) {
      next[tipo] = trimmed;
    } else {
      delete next[tipo];
    }
    try {
      await updateDoc(doc(db, "openData", "tiposVenues"), { tiposEn: next });
      setTiposEn(next);
      setEnDialogTipo(null);
      showAlert("Traducción guardada", "success");
    } catch (error) {
      console.error("Error saving translation:", error);
      showAlert("Error al guardar la traducción", "error");
    }
  };

  const openEnDialog = (tipo) => {
    setEnDialogValue(tiposEn[tipo] || "");
    setEnDialogTipo(tipo);
  };

  const openAddDialog = (arrayName, docName, fieldName) => {
    setCurrentAction({ arrayName, docName, fieldName, index: null });
    setIsAddDialogOpen(true);
  };

  const openDeleteDialog = (arrayName, index) => {
    setCurrentAction({ ...currentAction, arrayName, index });
    setIsDeleteDialogOpen(true);
  };

  const openSaveDialog = (arrayName, docName, fieldName) => {
    setCurrentAction({ arrayName, docName, fieldName, index: null });
    handleSave();
  };

  const ArraySection = ({ title, items, arrayName, docName, fieldName, enMap = null }) => (
    <div className="mb-8 p-6 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-4">
        <Heading level={3}>{title}</Heading>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => openAddDialog(arrayName, docName, fieldName)}
            color="dark"
            className="inline-flex items-center px-4 py-2 h-10"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            Agregar
          </Button>
          <Button
            onClick={() => openSaveDialog(arrayName, docName, fieldName)}
            color="dark"
            disabled={saving}
            className="px-4 py-2 h-10"
          >
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>
      </div>
      <p className="text-amber-600 mb-4 text-sm italic">
        Favor de mantener el orden apropiado, es el que los usuarios verán para
        filtrar.
      </p>
      <DragDropContext onDragEnd={(result) => handleDragEnd(result, arrayName)}>
        <Droppable droppableId={arrayName}>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-2"
            >
              {items.map((item, index) => (
                <Draggable
                  key={item}
                  draggableId={`${arrayName}-${item}`}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100"
                    >
                      <span>
                        {item}
                        {enMap && (
                          <span className={`ml-2 text-sm ${enMap[item] ? "text-gray-500" : "text-amber-600 italic"}`}>
                            {enMap[item] ? `EN: ${enMap[item]}` : "sin traducción EN"}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-3">
                        {enMap && (
                          <button
                            onClick={() => openEnDialog(item)}
                            className="text-gray-500 hover:text-[#1b4d2e]"
                            title="Editar traducción EN"
                          >
                            <LanguageIcon className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => openDeleteDialog(arrayName, index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </span>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );

  if (loading) {
    return <div className="p-4">Cargando...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Heading level={2} className="mb-6">
        Gestión de Datos del Sistema
      </Heading>

      <ArraySection
        title="Tipos de Venue"
        items={tiposVenue}
        arrayName="tipos"
        docName="tiposVenues"
        fieldName="tipos"
        enMap={tiposEn}
      />

      <ArraySection
        title="Capacidades"
        items={capacidades}
        arrayName="capacidades"
        docName="capacidad"
        fieldName="capacidades"
      />

      <ArraySection
        title="Zonas"
        items={zonas}
        arrayName="zonas"
        docName="zonas"
        fieldName="zonas"
      />

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)}>
        <DialogTitle>Agregar Nuevo Elemento</DialogTitle>
        <DialogBody>
          <DialogInput
            label="Valor"
            value={newItemValue}
            onChange={(e) => setNewItemValue(e.target.value)}
            placeholder="Ingrese el nuevo valor"
          />
        </DialogBody>
        <DialogActions>
          <Button color="zinc" onClick={() => setIsAddDialogOpen(false)}>
            Cancelar
          </Button>
          <Button color="dark" onClick={handleAddItem}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* EN Translation Dialog */}
      <Dialog open={enDialogTipo !== null} onClose={() => setEnDialogTipo(null)}>
        <DialogTitle>Traducción EN</DialogTitle>
        <DialogDescription>
          Traducción al inglés de “{enDialogTipo}”. Se guarda de inmediato;
          si se deja vacío, el sitio en inglés muestra el nombre en español.
        </DialogDescription>
        <DialogBody>
          <DialogInput
            label="Nombre en inglés"
            value={enDialogValue}
            onChange={(e) => setEnDialogValue(e.target.value)}
            placeholder="p. ej. Garden"
          />
        </DialogBody>
        <DialogActions>
          <Button color="zinc" onClick={() => setEnDialogTipo(null)}>
            Cancelar
          </Button>
          <Button color="dark" onClick={handleSaveEn}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogDescription>
          ¿Está seguro que desea eliminar este elemento?
        </DialogDescription>
        <DialogActions>
          <Button color="zinc" onClick={() => setIsDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleRemoveItem}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Success Dialog */}
      <Dialog
        open={isSaveDialogOpen}
        onClose={() => setIsSaveDialogOpen(false)}
      >
        <DialogTitle>Cambios Guardados</DialogTitle>
        <DialogDescription>
          Los cambios han sido guardados exitosamente.
        </DialogDescription>
        <DialogActions>
          <Button color="dark" onClick={() => setIsSaveDialogOpen(false)}>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OpenDataManager;
